import { Box, Divider, FormControl, FormHelperText, IconButton, InputLabel, Paper, Tooltip } from "@mui/material";
import { AtomicBlockUtils, ContentState, convertFromHTML, convertToRaw, Editor, EditorState, RichUtils } from "draft-js";
import { useRef, useState } from "react";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import CodeIcon from "@mui/icons-material/Code";

import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";

import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";

import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";

import draftjsToHtml from "draftjs-to-html";
import { useMessage } from "../../context/message-context";

/**
 * Liest eine Datei als Data-URL.
 */
function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Skaliert Bilder herunter, damit Handy-Fotos und grosse Screenshots
 * nicht als mehrere MB Base64 im HTML landen.
 */
async function fileToImageSrc(file, { maxDimension = 1600, quality = 0.82 } = {}) {
  const dataUrl = await readAsDataUrl(file);

  // Kleine Bilder unverändert übernehmen (behält u.a. PNG-Transparenz)
  if (file.size < 200 * 1024) return dataUrl;

  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Bild konnte nicht gelesen werden"));
    image.src = dataUrl;
  });

  const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);

  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff"; // JPEG kennt keine Transparenz
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL("image/jpeg", quality);
}

/**
 * Rendert atomic Blocks (aktuell nur Bilder).
 */
function AtomicBlock({ block, contentState }) {
  const entityKey = block.getEntityAt(0);
  if (!entityKey) return null;

  const entity = contentState.getEntity(entityKey);
  if (entity.getType() !== "IMAGE") return null;

  const { src, alt } = entity.getData();

  return <Box component="img" src={src} alt={alt || ""} sx={{ display: "block", maxWidth: "100%", height: "auto", borderRadius: 1, my: 1 }} />;
}

function EditorToolbar({ editorState, setEditorState, onPickImages }) {
  const fileInputRef = useRef(null);
  const currentStyle = editorState.getCurrentInlineStyle();

  const blockType = editorState.getCurrentContent().getBlockForKey(editorState.getSelection().getStartKey()).getType();

  const toggleInline = (style) => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  const toggleBlock = (type) => {
    setEditorState(RichUtils.toggleBlockType(editorState, type));
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        p: 1,
        borderBottom: 1,
        borderColor: "divider",
        flexWrap: "wrap",
      }}
    >
      {/* Inline */}
      <Tooltip title="Fett">
        <IconButton
          size="small"
          color={currentStyle.has("BOLD") ? "primary" : "default"}
          onMouseDown={(e) => {
            e.preventDefault();
            toggleInline("BOLD");
          }}
        >
          <FormatBoldIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Kursiv">
        <IconButton
          size="small"
          color={currentStyle.has("ITALIC") ? "primary" : "default"}
          onMouseDown={(e) => {
            e.preventDefault();
            toggleInline("ITALIC");
          }}
        >
          <FormatItalicIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Unterstrichen">
        <IconButton
          size="small"
          color={currentStyle.has("UNDERLINE") ? "primary" : "default"}
          onMouseDown={(e) => {
            e.preventDefault();
            toggleInline("UNDERLINE");
          }}
        >
          <FormatUnderlinedIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Durchgestrichen">
        <IconButton
          size="small"
          color={currentStyle.has("STRIKETHROUGH") ? "primary" : "default"}
          onMouseDown={(e) => {
            e.preventDefault();
            toggleInline("STRIKETHROUGH");
          }}
        >
          <StrikethroughSIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Code">
        <IconButton
          size="small"
          color={currentStyle.has("CODE") ? "primary" : "default"}
          onMouseDown={(e) => {
            e.preventDefault();
            toggleInline("CODE");
          }}
        >
          <CodeIcon />
        </IconButton>
      </Tooltip>

      <Divider flexItem orientation="vertical" />

      {/* Headings */}
      <Tooltip title="Überschrift 1">
        <IconButton
          size="small"
          color={blockType === "header-one" ? "primary" : "default"}
          onMouseDown={(e) => {
            e.preventDefault();
            toggleBlock("header-one");
          }}
        >
          H1
        </IconButton>
      </Tooltip>

      <Tooltip title="Überschrift 2">
        <IconButton
          size="small"
          color={blockType === "header-two" ? "primary" : "default"}
          onMouseDown={(e) => {
            e.preventDefault();
            toggleBlock("header-two");
          }}
        >
          H2
        </IconButton>
      </Tooltip>

      <Tooltip title="Zitat">
        <IconButton
          size="small"
          color={blockType === "blockquote" ? "primary" : "default"}
          onMouseDown={(e) => {
            e.preventDefault();
            toggleBlock("blockquote");
          }}
        >
          <FormatQuoteIcon />
        </IconButton>
      </Tooltip>

      <Divider flexItem orientation="vertical" />

      {/* Listen */}
      <Tooltip title="Aufzählung">
        <IconButton
          size="small"
          color={blockType === "unordered-list-item" ? "primary" : "default"}
          onMouseDown={(e) => {
            e.preventDefault();
            toggleBlock("unordered-list-item");
          }}
        >
          <FormatListBulletedIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Nummerierte Liste">
        <IconButton
          size="small"
          color={blockType === "ordered-list-item" ? "primary" : "default"}
          onMouseDown={(e) => {
            e.preventDefault();
            toggleBlock("ordered-list-item");
          }}
        >
          <FormatListNumberedIcon />
        </IconButton>
      </Tooltip>

      <Divider flexItem orientation="vertical" />

      {/* Bild einfügen (der zuverlässige Weg auf dem Handy) */}
      <Tooltip title="Bild einfügen">
        <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => fileInputRef.current?.click()}>
          <AddPhotoAlternateIcon />
        </IconButton>
      </Tooltip>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = ""; // gleiche Datei erneut wählbar machen
          if (files.length) onPickImages(files);
        }}
      />

      <Divider flexItem orientation="vertical" />

      {/* Undo / Redo */}
      <Tooltip title="Rückgängig">
        <IconButton
          size="small"
          onMouseDown={(e) => {
            e.preventDefault();
            setEditorState(EditorState.undo(editorState));
          }}
        >
          <UndoIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Wiederholen">
        <IconButton
          size="small"
          onMouseDown={(e) => {
            e.preventDefault();
            setEditorState(EditorState.redo(editorState));
          }}
        >
          <RedoIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default function DraftTextField({ label, value, onChange, helperText, minHeight = 500, error, uploadImage, maxImageDimension = 1600 }) {
  const editorRef = useRef(null);
  const { showErrorMessage } = useMessage();

  const handleChange = (newState) => {
    setEditorState(newState);

    const raw = convertToRaw(newState.getCurrentContent());
    const html = draftjsToHtml(raw);
    const final = html.replace(/<p>\s*<\/p>/g, "<p>&zwnj;</p>");

    onChange?.(final);
  };

  const insertImages = async (files) => {
    const images = files.filter((f) => f.type?.startsWith("image/"));
    if (!images.length) return;

    let state = editorState;

    for (const file of images) {
      try {
        const src = uploadImage ? await uploadImage(file) : await fileToImageSrc(file, { maxDimension: maxImageDimension });

        const contentWithEntity = state.getCurrentContent().createEntity("IMAGE", "IMMUTABLE", { src, alt: file.name || "" });
        const entityKey = contentWithEntity.getLastCreatedEntityKey();

        state = AtomicBlockUtils.insertAtomicBlock(EditorState.set(state, { currentContent: contentWithEntity }), entityKey, " ");
      } catch (err) {
        showErrorMessage("Fehler beim einfügen des Bildes. Bild möglicherweiße zu groß")
        console.error("Bild konnte nicht eingefügt werden", err);
      }
    }

    handleChange(EditorState.moveFocusToEnd(state));
  };

  const handlePastedFiles = (files) => {
    const list = Array.from(files || []);
    if (!list.some((f) => f.type?.startsWith("image/"))) return "not-handled";

    insertImages(list);
    return "handled";
  };

  const handleDroppedFiles = (_selection, files) => handlePastedFiles(files);

  const handleKeyCommand = (command, state) => {
    const newState = RichUtils.handleKeyCommand(state, command);
    if (newState) {
      handleChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  const blockRendererFn = (block) => {
    if (block.getType() === "atomic") {
      return { component: AtomicBlock, editable: false };
    }
    return null;
  };

  const createEditorState = () => {
    if (!value) return EditorState.createEmpty();

    const isHtml = /<[^>]+>/g.test(value); // Als erstes prüfen ob wir HTML oder Plain text haben

    if (isHtml) {
      const blocksFromHtml = convertFromHTML(value);
      const contentState = ContentState.createFromBlockArray(blocksFromHtml.contentBlocks, blocksFromHtml.entityMap);

      return EditorState.createWithContent(contentState); // Von HTML gewandelter Editor state
    }

    return EditorState.createWithContent(ContentState.createFromText(value)); // Plain text Editor State
  };

  const [editorState, setEditorState] = useState(createEditorState);

  return (
    <FormControl fullWidth error={error}>
      {label && (
        <InputLabel
          shrink
          sx={{
            position: "relative",
            transform: "none",
            mb: 1,
          }}
        >
          {label}
        </InputLabel>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          minHeight: minHeight,
          cursor: "text",
        }}
        onClick={() => editorRef.current?.focus()}
      >
        <EditorToolbar editorState={editorState} setEditorState={setEditorState} onPickImages={insertImages} />
        <Editor
          ref={editorRef}
          editorState={editorState}
          onChange={handleChange}
          handleKeyCommand={handleKeyCommand}
          handlePastedFiles={handlePastedFiles}
          handleDroppedFiles={handleDroppedFiles}
          blockRendererFn={blockRendererFn}
        />
      </Paper>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
